import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "../../../css/CreateEvent.css";
import "../../../css/FullEvent/EventDashboard.css";
import "../../../css/GlobalStyles.css";
import {
  AccountData,
  Event,
  OrganizerType,
  PlanTypeV4,
  TicketV2,
  NotificationType,
} from "@markit/common.types";
import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import {
  filterUndefinedValues,
  hasSubscriptionUnlockedAdvancedData,
  hostedOnlyEvents,
  hostingOnlyEvents,
  isEventTicketsPaid,
  isRevShareOrganizerTicket,
  uniqueVals,
} from "@markit/common.utils";
import { useTheme } from "../../../hooks/useTheme";
import FlatList from "flatlist-react/lib";
import { API } from "../../../API";
import NumberInput from "../../NumberInput";
import SwitchToggleItem from "../../SwitchToggleItem";
import { MixpanelContext } from "../../../context/AnalyticsService";
import { useDispatch, useSelector } from "react-redux";
import {
  accountActions,
  getAccountState,
} from "../../../redux/slices/accountSlice";
import { getEventState } from "../../../redux/slices/eventSlice";
import SelectItemRow from "../../Buttons/SelectItemRow";
import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import BlackHoverButton from "../../Buttons/BlackHoverButton";
import { fetchSingleUser } from "../../../utils/FetchSingleData";
import filter from "lodash.filter";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import { SelectRecipientItem } from "../../DisplayItem/SelectRecipientItem";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import AlertContainer from "../../Containers/AlertContainer";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { CircularProgress } from "@mui/material";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import { getTotalRevShareTicketsSoldByTicket } from "../../../utils/eventUtils/eventUtils";

interface EventRoleModalProps {
  event: Event;
  organizerTicketItem: TicketV2 | undefined;
  organizerData: AccountData | undefined;
  setOrganizerTicketItem: (
    value: React.SetStateAction<TicketV2 | undefined>
  ) => void;
  setOrganizerData: (
    value: React.SetStateAction<AccountData | undefined>
  ) => void;
  isEventRoleModalOpen: boolean;
  setIsEventRoleModalOpen: (formsModalOpen: boolean) => void;
  setProfilePreviewSelected: (profilePreviewSelected: AccountData) => void;
}

const EventRoleModal = (props: EventRoleModalProps) => {
  const {
    event,
    organizerTicketItem,
    organizerData,
    setOrganizerTicketItem,
    setOrganizerData,
    isEventRoleModalOpen,
    setIsEventRoleModalOpen,
    setProfilePreviewSelected,
  } = props;
  const mixpanel = useContext(MixpanelContext);
  const { theme } = useTheme();
  const { account } = useSelector(getAccountState);
  const { accountData, followingAccountData } = account;
  const { events: eventState } = useSelector(getEventState);
  const { events } = eventState;
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [currScreen, setCurrScreen] = useState<number>(0);
  const [selectedRole, setSelectedRole] = useState<OrganizerType>(
    organizerTicketItem ? organizerTicketItem.role.type : OrganizerType.COHOST
  );
  const [isAlternatePaymentCollector, setIsAlternatePaymentCollector] =
    useState(
      organizerTicketItem
        ? organizerTicketItem.role.alternatePaymentCollector
        : false
    );
  const [promoterType, setPromoterType] = useState<
    "percent" | "dollars" | undefined
  >(undefined);
  const [revenueSplit, setRevenueSplit] = useState<number | undefined>();
  const [selectedRoleUser, setSelectedRoleUser] = useState<AccountData>();
  const [searchTerm, setSearchTerm] = useState("");
  const [recentRoleUserData, setRecentRoleUserData] = useState<AccountData[]>(
    []
  );
  const [hasSoldRevShareTickets, setHasSoldRevShareTickets] = useState(false);

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [exitConfirm, setExitConfirm] = useState(false);

  const hasPaidTickets: boolean = useMemo(
    () => isEventTicketsPaid(event),
    [event]
  );

  const isEditingRole = useMemo(
    () => organizerData !== undefined,
    [organizerData]
  );

  const alternatePaymentCollectorDescription = useMemo(
    () =>
      !hasPaidTickets
        ? "Available for paid ticketed events"
        : organizerTicketItem &&
            organizerTicketItem.role.alternatePaymentCollector &&
            event.stripePaymentCollector !== ""
          ? "You already have a collector set"
          : "Divert payments to a user for this event",
    [event.stripePaymentCollector, hasPaidTickets, organizerTicketItem]
  );

  const revenueSharingDescription = useMemo(
    () =>
      hasPaidTickets
        ? "Share a set percentage or dollar amount of sales when the user uses their own share link"
        : "Available for paid ticketed events",
    [hasPaidTickets]
  );

  const canSetPaymentCollector: boolean = useMemo(
    () => hasPaidTickets && event.stripePaymentCollector === "",
    [event.stripePaymentCollector, hasPaidTickets]
  );

  const isPromoterRelatedRole = useMemo(
    () =>
      selectedRole === OrganizerType.PROMOTER ||
      selectedRole === OrganizerType.COHOST,
    [selectedRole]
  );

  const styles = {
    buttonView: {
      whiteSpace: "nowrap",
      borderRadius: 100,
      paddingBlock: 10,
      paddingInline: 12,
    },
    inputContainer: { padding: "10px 14px", borderRadius: 12, width: 100 },
  };

  useAsyncOnMount(async () => {
    // If editing role and the organizer is a rev share organizer, check if they've sold any tickets yet
    // We disable editing the role details at all if they've sold any rev share tickets
    if (
      isEditingRole &&
      organizerTicketItem &&
      isRevShareOrganizerTicket(organizerTicketItem)
    ) {
      const numRevShareTicketsSold = await getTotalRevShareTicketsSoldByTicket(
        event.id,
        organizerTicketItem.id
      );
      setHasSoldRevShareTickets(numRevShareTicketsSold > 0);
    }
  });

  useEffect(() => {
    if (organizerData && organizerTicketItem) {
      setSelectedRoleUser(organizerData);
      setSelectedRole(organizerTicketItem.role.type);
      setRevenueSplit(
        organizerTicketItem.role.revenuePercentSplit ||
          organizerTicketItem.role.revenueDollarSplit / 100
      );
      setPromoterType(
        organizerTicketItem.role.revenuePercentSplit
          ? "percent"
          : organizerTicketItem.role.revenueDollarSplit
            ? "dollars"
            : undefined
      );
      setIsAlternatePaymentCollector(
        organizerTicketItem.role.alternatePaymentCollector
      );
    }
  }, [organizerData, organizerTicketItem, setIsEventRoleModalOpen]);

  const clearFields = useCallback(() => {
    setIsEventRoleModalOpen(false);
    setCurrScreen(0);
    setSelectedRoleUser(undefined);
    setOrganizerTicketItem(undefined);
    setOrganizerData(undefined);
    setSelectedRole(OrganizerType.COHOST);
    setRevenueSplit(0);
    setIsAlternatePaymentCollector(false);
  }, [setIsEventRoleModalOpen, setOrganizerData, setOrganizerTicketItem]);

  const nextOnPress = useCallback(() => {
    if (revenueSplit) {
      if (
        promoterType === "percent" &&
        (!Number.isInteger(revenueSplit) ||
          revenueSplit < 1 ||
          revenueSplit > 99)
      ) {
        setAlertText({
          heading: "Invalid revenue split",
          subHeading: "Revenue split must be a full integer between 1% and 99%",
        });
        return;
      } else if (
        promoterType === "dollars" &&
        (revenueSplit < 1 || revenueSplit > 1000)
      ) {
        setAlertText({
          heading: "Invalid revenue split",
          subHeading:
            "Revenue split must be a dollar amount between 1 and 1000",
        });
        return;
      }
    } else if (selectedRole === OrganizerType.PROMOTER && !promoterType) {
      setAlertText({
        heading: "No revenue split specified",
        subHeading:
          "Please specify a revenue split or select a different role.",
      });
      return;
    }
    setCurrScreen(1);
  }, [promoterType, revenueSplit, selectedRole]);

  const verifyEventRole = useCallback(
    async (isUnassigned: boolean) => {
      // check if selected user already has a role
      const finalSelectedRoleUserId = isUnassigned ? "" : selectedRoleUser?.uid;
      const alreadyHasRole =
        finalSelectedRoleUserId &&
        // TODO: This will need to be refactored when we deprecated these arrays.
        // Ticket data and access levels will be needed
        (event.cohosts.includes(finalSelectedRoleUserId) ||
          event.scanners.includes(finalSelectedRoleUserId) ||
          event.performers.includes(finalSelectedRoleUserId) ||
          event.promoters.includes(finalSelectedRoleUserId));
      if (alreadyHasRole && !isEditingRole) {
        setAlertText({
          heading: "This user cannot be selected.",
          subHeading: "They already have a role.",
        });
        return false;
      }
      return true;
    },
    [
      event.cohosts,
      event.performers,
      event.promoters,
      event.scanners,
      isEditingRole,
      selectedRoleUser?.uid,
    ]
  );

  const saveEventRolesPress = useCallback(
    async (isUnassigned: boolean) => {
      const isVerified = await verifyEventRole(isUnassigned);
      if (!isVerified) {
        return;
      }
      const finalSelectedRoleUserId = isUnassigned ? "" : selectedRoleUser?.uid;
      await API.events.createOrganizerTickets({
        event: event,
        selectedRoleUser: finalSelectedRoleUserId || "",
        organizerType: selectedRole,
        isPaymentCollector: isAlternatePaymentCollector,
        revenuePercentSplit:
          isPromoterRelatedRole && promoterType === "percent" && revenueSplit
            ? revenueSplit
            : 0,
        revenueDollarSplit:
          isPromoterRelatedRole && promoterType === "dollars" && revenueSplit
            ? revenueSplit * 100 // in cents
            : 0,
      });
      clearFields();
      mixpanel.track("Assigned Event Role", {
        event_id: event.id,
        selected_user_id: finalSelectedRoleUserId,
        is_unassigned: isUnassigned,
        organizer_role: selectedRole,
        is_payment_collector: isAlternatePaymentCollector,
        revenue_split: revenueSplit,
        promoter_type: promoterType,
      });
      showNotificationBanner(
        dispatch,
        "Role Created!",
        NotificationType.AFFIRMATIVE
      );
    },
    [
      clearFields,
      dispatch,
      event,
      isAlternatePaymentCollector,
      isPromoterRelatedRole,
      mixpanel,
      promoterType,
      revenueSplit,
      selectedRole,
      selectedRoleUser?.uid,
      verifyEventRole,
    ]
  );

  const toggleRevSharingOption = useCallback(
    (type: "percent" | "dollars") => {
      if (promoterType === type) {
        setRevenueSplit(0);
        setPromoterType(undefined);
      } else {
        setRevenueSplit(type === "percent" ? 20 : 5);
        setPromoterType(type);
      }
    },
    [promoterType, setRevenueSplit, setPromoterType]
  );

  const revenueShareComponent = (
    <div
      className="ColumnNormal"
      style={{
        gap: 14,
        opacity: hasSoldRevShareTickets ? 0.5 : 1,
        pointerEvents: hasSoldRevShareTickets ? "none" : "all",
      }}
    >
      <SwitchToggleItem
        title="Percent Shared"
        description="Share a percentage of revenue with the user"
        toggleValue={promoterType === "percent"}
        onChange={() => toggleRevSharingOption("percent")}
        theme={theme}
      />
      {promoterType === "percent" ? (
        <div className="AlignedRowSpaced">
          <div className="bodyMedium" style={{ ...theme.PrimaryText }}>
            Percent Revenue Shared
          </div>
          <NumberInput
            min={1}
            max={99}
            prefix={"%"}
            value={revenueSplit}
            onChange={setRevenueSplit}
            containerStyles={styles.inputContainer}
          />
        </div>
      ) : null}
      <SwitchToggleItem
        title="Dollars Shared"
        description="Share a fixed-dollar amount of revenue per ticket with the user"
        toggleValue={promoterType === "dollars"}
        onChange={() => toggleRevSharingOption("dollars")}
        theme={theme}
      />
      {promoterType === "dollars" ? (
        <div className="AlignedRowSpaced">
          <div className="bodyMedium" style={{ ...theme.PrimaryText }}>
            Dollar Amount
          </div>
          <NumberInput
            min={1}
            max={1000}
            prefix={"$"}
            value={revenueSplit}
            onChange={(value) => {
              const roundedValue = Number(value?.toFixed(2));
              setRevenueSplit(roundedValue);
            }}
            containerStyles={styles.inputContainer}
            allowDecimal
          />
        </div>
      ) : null}
    </div>
  );

  const madeChanges = useMemo(
    () =>
      organizerTicketItem
        ? organizerTicketItem.role.type !== selectedRole ||
          organizerTicketItem.role.alternatePaymentCollector !==
            isAlternatePaymentCollector ||
          organizerTicketItem.role.revenuePercentSplit !== revenueSplit
        : false,
    [
      isAlternatePaymentCollector,
      organizerTicketItem,
      revenueSplit,
      selectedRole,
    ]
  );

  const navigateSaveOnPress = useCallback(async () => {
    if (organizerTicketItem) {
      try {
        const isVerified = await verifyEventRole(false);
        if (!isVerified) {
          return;
        }
        await API.events.updateOrganizerTickets({
          event: event,
          userId: organizerTicketItem.uid,
          ticketId: organizerTicketItem.id,
          selectedRole: selectedRole,
          isAlternatePaymentCollector: isAlternatePaymentCollector,
          percentSplit:
            isPromoterRelatedRole && promoterType === "percent" && revenueSplit
              ? revenueSplit
              : 0,
          dollarSplit:
            isPromoterRelatedRole && promoterType === "dollars" && revenueSplit
              ? revenueSplit * 100 // in cents
              : 0,
          previousRole: organizerTicketItem.role,
        });
        clearFields();
        showNotificationBanner(
          dispatch,
          "Role Updated!",
          NotificationType.AFFIRMATIVE
        );
      } catch (err: any) {
        console.error(err.message);
      }
    }
  }, [
    clearFields,
    dispatch,
    event,
    isAlternatePaymentCollector,
    isPromoterRelatedRole,
    organizerTicketItem,
    promoterType,
    revenueSplit,
    selectedRole,
    verifyEventRole,
  ]);

  const closeOrBackOnPress = useCallback(async () => {
    if (currScreen === 1) {
      setCurrScreen(0);
    } else {
      if (madeChanges) {
        setExitConfirm(true);
      } else {
        clearFields();
      }
    }
  }, [clearFields, currScreen, madeChanges]);

  // Assigning role logic
  const recentRoles = useMemo(
    () =>
      uniqueVals(
        hostingOnlyEvents(events, accountData.uid)
          .concat(hostedOnlyEvents(events, accountData.uid))
          .map((event) =>
            event.cohosts
              .concat(event.scanners)
              .concat(event.promoters)
              .concat(
                event.stripePaymentCollector ? event.stripePaymentCollector : []
              )
          )
          .flat()
          .filter(
            (r) =>
              !event.cohosts.includes(r) &&
              !event.scanners.includes(r) &&
              !event.promoters.includes(r) &&
              event.stripePaymentCollector !== r
          )
      ),
    [
      accountData.uid,
      event.cohosts,
      event.promoters,
      event.scanners,
      event.stripePaymentCollector,
      events,
    ]
  );

  const handleSearch = useCallback((text: string) => {
    setSearchTerm(text.toLowerCase());
  }, []);

  const containsUser = (user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query.toLowerCase());
  };

  const itemsToShow = useMemo(() => {
    if (searchTerm === "") {
      return recentRoleUserData;
    }

    const userList: AccountData[] = filter(
      recentRoleUserData,
      (user: AccountData) => {
        return containsUser(user, searchTerm);
      }
    );

    return userList;
  }, [recentRoleUserData, searchTerm]);

  useAsyncOnMount(async () => {
    const users = await Promise.all(
      recentRoles.map((userId) => fetchSingleUser(userId, followingAccountData))
    );
    const usersDefined: AccountData[] = filterUndefinedValues(users);
    dispatch(accountActions.addMultipleFollowingAccountData(usersDefined));
    setRecentRoleUserData(
      usersDefined.sort((a, b) => {
        if (a.fullName.toLowerCase() > b.fullName.toLowerCase()) {
          return 1;
        }
        if (b.fullName.toLowerCase() > a.fullName.toLowerCase()) {
          return -1;
        }
        return 0;
      })
    );
    setLoading(false);
  });

  const renderUpgradeButton = useMemo(
    () => (
      <span className="smallBodySubtext">
        Requires Markit
        <span style={{ color: Colors.BLUE5 }}>+ </span>
        Pro Plan.{" "}
        <span
          onClick={(e) => {
            e.stopPropagation();
            dispatch(
              notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name)
            );
          }}
          style={{ color: Colors.BLUE5, cursor: "pointer" }}
        >
          Upgrade
        </span>
      </span>
    ),
    [dispatch]
  );

  return (
    <>
      <LargePopupModalContainer
        showModal={isEventRoleModalOpen}
        headerComp={
          <div className="AlignedRowSpaced">
            <div className="LargePopupPanelClose" onClick={closeOrBackOnPress}>
              <Icon
                icon={currScreen === 0 ? "mdi:close" : "ion:chevron-back"}
                height={24}
              />
            </div>
            <div className="AlignedRow" style={{ gap: 10 }}>
              {currScreen === 0 ? (
                organizerTicketItem ? (
                  <BlackHoverButton
                    title="Save"
                    onPress={() => navigateSaveOnPress()}
                    containerStyles={styles.buttonView}
                    permanentHover
                  />
                ) : (
                  <BlackHoverButton
                    title="Next"
                    onPress={nextOnPress}
                    iconName="ion:arrow-forward-circle"
                    iconSize={17}
                    containerStyles={styles.buttonView}
                    permanentHover
                  />
                )
              ) : (
                <>
                  {!loading ? (
                    <>
                      <BlackHoverButton
                        title="Save & Skip"
                        onPress={() => saveEventRolesPress(true)}
                        containerStyles={styles.buttonView}
                      />
                      {itemsToShow.length > 0 ? (
                        <BlackHoverButton
                          title="Assign"
                          onPress={() => saveEventRolesPress(false)}
                          containerStyles={styles.buttonView}
                          permanentHover
                          disabled={!selectedRoleUser}
                        />
                      ) : null}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        }
        valueComp={
          <div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>
              {currScreen === 1
                ? "Assign Role"
                : organizerTicketItem && organizerData
                  ? "Edit " + organizerData.fullName + "'s role"
                  : organizerTicketItem
                    ? "Edit unassigned role"
                    : "Select Organizer Role"}
            </div>
            <div
              style={{
                marginTop: 7,
                fontSize: 14,
                color: theme.SubText.color,
              }}
            >
              {currScreen === 1
                ? "Assign this organizer role to a user below. Save & Skip to share a link to this role with new organizers."
                : organizerTicketItem
                  ? "Options shown from highest to lowest dashboard access level."
                  : "Choose the organizer role you want to add. Options shown from highest to lowest dashboard access level."}
            </div>
            {currScreen === 0 ? (
              // TODO (jonathan): this gray box is a candidate to turn into a component if used more often
              <div
                className="ColumnNormal"
                style={{
                  gap: 24,
                  marginTop: 20,
                  paddingBlock: 24,
                  paddingInline: 14,
                  backgroundColor: theme.PrimaryBG.backgroundColor,
                  borderRadius: 12,
                }}
              >
                <SelectItemRow
                  title="Co-Host"
                  description={
                    "Allows for full editing capabilities, mass texting using their audience, and event dashboard access. Also will be listed on event as a Co-Host."
                  }
                  onPress={() => {
                    if (selectedRole !== OrganizerType.COHOST) {
                      setIsAlternatePaymentCollector(false);
                      setRevenueSplit(0);
                      setPromoterType(undefined);
                      setSelectedRole(OrganizerType.COHOST);
                    }
                  }}
                  disabled={hasSoldRevShareTickets}
                  selected={selectedRole === OrganizerType.COHOST}
                  subComp={
                    <div>
                      <SwitchToggleItem
                        title="Make alternate payment collector"
                        description={alternatePaymentCollectorDescription}
                        disabled={
                          hasSoldRevShareTickets ||
                          (!(
                            organizerTicketItem &&
                            organizerTicketItem.role.alternatePaymentCollector
                          ) &&
                            !canSetPaymentCollector)
                        }
                        toggleValue={isAlternatePaymentCollector}
                        onChange={() => {
                          if (revenueSplit) {
                            setPromoterType(undefined);
                            setRevenueSplit(0);
                          }
                          setIsAlternatePaymentCollector(
                            !isAlternatePaymentCollector
                          );
                        }}
                      />
                      <div style={{ marginTop: 24 }}>
                        <div>
                          <SwitchToggleItem
                            title="Enable Revenue Sharing"
                            description={revenueSharingDescription}
                            disabled={
                              !hasPaidTickets ||
                              hasSoldRevShareTickets ||
                              !hasSubscriptionUnlockedAdvancedData(accountData)
                            }
                            toggleValue={!!promoterType}
                            onChange={() => {
                              if (isAlternatePaymentCollector) {
                                setIsAlternatePaymentCollector(false);
                              }
                              setPromoterType(
                                promoterType ? undefined : "percent"
                              );
                              setRevenueSplit(promoterType ? 0 : 20);
                            }}
                          />
                          {!hasSubscriptionUnlockedAdvancedData(accountData) &&
                          hasPaidTickets
                            ? renderUpgradeButton
                            : null}
                          {promoterType ? (
                            <div style={{ marginTop: 20 }}>
                              {revenueShareComponent}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  }
                />
                <SelectItemRow
                  title="Scanner"
                  description="Scanning and guest list check in, but limited event dashboard access. No editing, mass texting, or revenue visibility. Also not listed on event."
                  onPress={() => {
                    if (selectedRole !== OrganizerType.SCANNER) {
                      setIsAlternatePaymentCollector(false);
                      setSelectedRole(OrganizerType.SCANNER);
                    }
                  }}
                  disabled={hasSoldRevShareTickets}
                  selected={selectedRole === OrganizerType.SCANNER}
                />
                <SelectItemRow
                  title="Promoter"
                  description={revenueSharingDescription}
                  onPress={() => {
                    if (
                      selectedRole !== OrganizerType.PROMOTER &&
                      hasPaidTickets &&
                      hasSubscriptionUnlockedAdvancedData(accountData)
                    ) {
                      setIsAlternatePaymentCollector(false);
                      setRevenueSplit(20);
                      setSelectedRole(OrganizerType.PROMOTER);
                      setPromoterType("percent");
                    }
                  }}
                  selected={
                    selectedRole === OrganizerType.PROMOTER && hasPaidTickets
                  }
                  subComp={revenueShareComponent}
                  disabled={
                    !hasPaidTickets ||
                    hasSoldRevShareTickets ||
                    !hasSubscriptionUnlockedAdvancedData(accountData)
                  }
                  subAlwaysComp={
                    !hasSubscriptionUnlockedAdvancedData(accountData) &&
                    hasPaidTickets
                      ? renderUpgradeButton
                      : undefined
                  }
                />
                <SelectItemRow
                  title="Alternate Payment Collector"
                  description={alternatePaymentCollectorDescription}
                  onPress={() => {
                    if (
                      selectedRole !==
                        OrganizerType.ALTERNATEPAYMENTCOLLECTOR &&
                      ((organizerTicketItem &&
                        organizerTicketItem.role.alternatePaymentCollector) ||
                        canSetPaymentCollector)
                    ) {
                      setIsAlternatePaymentCollector(true);
                      setSelectedRole(OrganizerType.ALTERNATEPAYMENTCOLLECTOR);
                    }
                  }}
                  selected={
                    selectedRole === OrganizerType.ALTERNATEPAYMENTCOLLECTOR &&
                    ((organizerTicketItem &&
                      organizerTicketItem.role.alternatePaymentCollector) ||
                      canSetPaymentCollector)
                  }
                  disabled={!canSetPaymentCollector || hasSoldRevShareTickets}
                />
                {/* @TODO: Once Performer is implemented uncomment the following */}
                {/* <SelectItemRow
                  title="Performer"
                  description={
                    '(Coming soon) No editing abilities, scanning, or dashboard access. Just adds label as "performer" to guest list.'
                  }
                  onPress={() => {}}
                  selected={false}
                  disabled={true}
                /> */}
              </div>
            ) : (
              <>
                {!loading ? (
                  <div>
                    <SearchBoxContainer
                      placeholder={"Search"}
                      onChange={(e) => handleSearch(e.target.value)}
                      containerStyles={{ marginBlock: 20 }}
                    />
                    <div
                      style={{
                        height: "calc(100vh - 250px)",
                        overflowY: "scroll",
                      }}
                      className="HideScrollbar"
                    >
                      <FlatList
                        list={itemsToShow}
                        scrollEnabled={false}
                        keyExtractor={(item: AccountData) => item.uid}
                        renderItem={(userData, index) => {
                          const selectedUserId = selectedRoleUser
                            ? selectedRoleUser.uid
                            : "";
                          const selected = selectedUserId === userData.uid;

                          return (
                            <SelectRecipientItem
                              item={userData}
                              isSelected={selected}
                              iconColor={Colors.BLUE5}
                              selectOnPress={() => {
                                if (
                                  selectedRoleUser &&
                                  selectedRoleUser.uid === userData.uid
                                ) {
                                  setSelectedRoleUser(undefined);
                                } else {
                                  setSelectedRoleUser(userData);
                                }
                              }}
                              selectPreviewOnPress={() =>
                                setProfilePreviewSelected(userData)
                              }
                            />
                          );
                        }}
                        renderWhenEmpty={() =>
                          recentRoleUserData.length === 0 ? (
                            <EmptyStateButton
                              title={"No Recently Assigned"}
                              btnText="Save & Skip"
                              description={
                                "You have never assigned a role before. Save & Skip to share a link to this role with new organizers."
                              }
                              icon={
                                <Icon
                                  icon={"ion:people"}
                                  height={49}
                                  style={{ color: Colors.GRAY1 }}
                                />
                              }
                              iconBox={73}
                              onPress={() => saveEventRolesPress(true)}
                              containerStyles={{ paddingTop: 120 }}
                            />
                          ) : itemsToShow.length === 0 ? (
                            <EmptyStateButton
                              title="No Results"
                              btnText="Save & Skip"
                              description="Check for typos or Save & Skip to share a link to this role with new organizers."
                              icon={
                                <Icon
                                  icon="ion:search"
                                  height={40}
                                  style={{ color: Colors.GRAY1 }}
                                />
                              }
                              onPress={() => saveEventRolesPress(true)}
                              containerStyles={{ paddingTop: 120 }}
                            />
                          ) : null
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="Centering" style={{ height: "80vh" }}>
                    <CircularProgress
                      style={{ color: Colors.GRAY1 }}
                      size={30}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        }
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
      <ConfirmDeleteModal
        heading="If you exit, your changes will be discarded."
        subtext="Continue?"
        deleteButtonText="Discard Changes"
        hideModal={!exitConfirm}
        setIsVisible={setExitConfirm}
        deleteOnPress={() => clearFields()}
      />
    </>
  );
};

export default EventRoleModal;
